import { FiX } from "react-icons/fi";
import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { Item } from "types/common";
import { getTagData } from "api/tags";
import classNames from "utilities/ClassNames";
import Card from "../shared/Card/Card";
import Gauge from "components/charts/Gauge/Gauge";
import Area from "components/charts/Area/Area";
import areaApiConfig from "config/charts/areaApiConfig";

type BlockData = {
  id: number;
  feed: 'A' | 'B';
  status: 'On' | 'Off' | 'Tripped';
  load: '25A/80%';
  rack: number;
};

const tabs = [
  {
    id: 'genInfo',
    label: 'General Informatio',
    content: null
  },
];

const tableTabs = [
  {
    id: 'graph',
    label: 'Graph',
    content: null
  },
  {
    id: 'alarm',
    label: 'Alarm List',
    content: null
  }
];

const Table = ({ data }: { data: BlockData[] }) => {
  return (
    <div className="w-full h-full max-h-full rounded-lg border border-[#134366] bg-[#1D4A7A] p-1">
      {/* Header */}
      <div className="grid grid-cols-5 text-white font-bold bg-[#1A3A5F] p-2 rounded-t-lg">
        <div>Circuit ID</div>
        <div>Feed (A/B)</div>
        <div>Status</div>
        <div>Load (A/$)</div>
        <div>Rack/PDU</div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-1 mt-1 h-full max-h-full">
        {data.map((row, idx) => (
          <div
            key={row.id}
            className={classNames(
              'grid grid-cols-5 w-full px-2 py-0 xl-2xl:py0.5 2xl:py-1 text-white rounded-xl',
              (idx === 2 || idx === 8)
                ? 'bg-[#FDBD4D]/40 border border-[#FDBD4D]'
                : idx === 15
                  ? 'bg-[#FF5233]/40 border border-[#FF5233]'
                  : 'bg-[#175E92]'
            )}
          >
            <div>{row.id}</div>
            <div>{row.feed}</div>
            <div>{row.status}</div>
            <div>{row.load}</div>
            <div>{row.rack}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RPPModal = ({ setShowModal }: { setShowModal: Dispatch<SetStateAction<boolean>> }) => {

  const [randomTag, setRandomTag] = useState<Item | null>(null);
  const [selectedTab, setSelectedTab] = useState('genInfo');
  const [serverTableData, setServerTableData] = useState<any[]>([]);
  const [selectedTableTab, setSelectedTableTab] = useState('graph');
  const [graphName, setGraphName] = useState('Power Consumption');
  const [selectedSlot, setSelectedSlot] = useState('Server');

  const getSingleTag = async () => {
    const randomTagData = await getTagData([244]);
    setRandomTag(randomTagData.data[0]);
  };
  const serverData = [
    { label: "Device name", value: `Server-HP-DL360p` },
    { label: "Type", value: `Server` },
    { label: "Model HP", value: "PrioLiant DL360p Gen8" },
    { label: "Serial Number", value: "SN12345678" },
    { label: "Vendor", value: "Hewlett-Packard" },
  ];

  useEffect(() => {
    setServerTableData(serverData);
  }, [graphName, selectedSlot]);

  useEffect(() => {
    if (selectedTab === 'genInfo') {
      setServerTableData(serverData);
    } else {
      setServerTableData([]);
    }
  }, [selectedTab]);

  useEffect(() => {
    getSingleTag();
  }, []);

  const blockData: BlockData[] = Array.from({ length: 19 }).map((_, i) => ({
    id: i + 1,
    feed: i % 2 ? 'A' : 'B',
    status: i === 5 ? 'Tripped' : i % 2 ? 'On' : 'Off',
    load: '25A/80%',
    rack: i + 1
  }));

  return (
    <div className="h-screen w-screen bg-black/65 flex items-center justify-center absolute left-0 top-0 z-50">
      <div className="h-[90%] max-w-[1000px] xl-2xl:max-w-[1100px] 2xl:max-w-[1500px] w-full bg-black px-3 py-2">
        <div className="flex gap-2 max-h-full h-full">
          <div className="flex-1 h-full flex flex-col">
            <div className="bg-dark/85 relative w-full p-1 px-2 rounded flex h-max">
              <h2 className="2xl:text-2xl w-full text-center">UPS 1-String 1-43 Blocks</h2>
            </div>
            <div className="flex gap-2 w-full flex-1 overflow-hidden h-full">
              <div className="h-full flex-1 bg-dark rounded-md">
                <div className="h-full p-2">
                  <Table data={blockData} />
                </div>
              </div>
            </div>
          </div>
          <div className="h-full max-h-full w-[40%] flex flex-col">
            <div className="w-full">
              {randomTag && <Card title="RPP - 01" className="w-[50%] relative">
                <div className="absolute right-2 h-max top-2 flex items-center justify-center">
                  <button
                    className="cursor-pointer bg-blue rounded-md p-0.5 2xl:p-1"
                    onClick={() => setShowModal(false)}
                  >
                    <FiX size={20} />
                  </button>
                </div>
                <div className="flex max-h-[17vh] min-h-0">
                  <div>
                    <Gauge
                      data={{ ...randomTag, name: 'Input Voltage' }}
                      gaugeType={"regular"}
                    />
                  </div>
                  <div>
                    <Gauge
                      data={{ ...randomTag, name: 'Input Current' }}
                      gaugeType={"regular"}
                    />
                  </div>
                  <div>
                    <Gauge
                      data={{ ...randomTag, name: 'Input Power' }}
                      gaugeType={"regular"}
                    />
                  </div>
                </div>
              </Card>}
            </div>
            <div className="flex flex-col px-2 bg-dark p-1 rounded-lg">
              <div className="flex gap-2 w-full min-h-0 p-1 px-0 pb-2" style={{ flex: 1 }}>
                <div className="w-[35%]">
                  <Card title="Total output load">
                    <div className="flex gap-1">
                      <div className="p-1 h-full w-full">
                        <div className="text-1xl text-green h-[70%] pb-2">37.7kW</div>
                      </div>
                      <div className="p-1 h-full w-full">
                        <div className="text-1xl text-green h-[70%] pb-2">37.7%</div>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="flex-1">
                  <Card title="Numbers of Active Breakers">
                    <div className="p-1.5 h-full w-full flex justify-around">
                      <div className="h-[70%] flex gap-2">
                        <div className="text-1xl">ON:</div>
                        <div className="text-1xl text-green pb-1">37</div>
                      </div>
                      <div className="h-[70%] flex gap-2">
                        <div className="text-1xl">OFF:</div>
                        <div className="text-1xl text-green pb-1">37</div>
                      </div>
                      <div className="h-[70%] flex gap-2">
                        <div className="text-1xl">Tripped:</div>
                        <div className="text-1xl text-green pb-1">37</div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
              <div className="w-full pb-1">
                <Card title="Load per Phase">
                  <div className="px-1 2xl:p-1 h-full w-full flex justify-around">
                    <div className="h-[70%] flex gap-2 flex-col">
                      <div className="text-sm xl-2xl:text-1xl">Phase A</div>
                      <div className="flex gap-2">
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7kW</div>
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7%</div>
                      </div>
                    </div>
                    <div className="h-[70%] flex gap-2 flex-col">
                      <div className="text-sm xl-2xl:text-1xl">Phase B</div>
                      <div className="flex gap-2">
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7kW</div>
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7%</div>
                      </div>
                    </div>
                    <div className="h-[70%] flex gap-2 flex-col">
                      <div className="text-sm xl-2xl:text-1xl">Phase C</div>
                      <div className="flex gap-2">
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7kW</div>
                        <div className="text-1xl text-green pb-2 xl-2xl:pb-4">37.7%</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <div>
              <div className="">
                <div className="py-1 2xl:py-2 bg-black rounded-lg px-4 flex flex-col overflow-hidden">
                  <div className="flex gap-2 mb-2">
                    {tabs.map((tab) =>
                      <div onClick={() => setSelectedTab(tab.id)} key={tab.id} className={`px-2 py-0.5 2xl:py-1  bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer ${selectedTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                        <h3 className="text-[12px] font-medium">{tab.label}</h3>
                      </div>
                    )}
                  </div>
                  <table className="w-full min-h-[120px] 2xl:min-h-[130px] overflow-y-scroll">
                    <tbody className="w-full px-4 h-full">
                      {serverTableData && serverTableData.map(({ label, value }, index) => (
                        <tr
                          key={label}
                          className={`px-2 w-full ${index !== serverData.length - 1 ? "border-b border-[#3b5c74]" : ""}`}
                        >
                          <td className="p-0 text-xs 2xl:text-[16px]">{label}</td>
                          <td className="p-0 text-end text-xs 2xl:text-[16px]">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {randomTag &&
              <div className="flex-1 overflow-hidden">
                <div className={classNames(selectedTableTab === 'graph' ? "" : "h-full", "py-1 bg-black rounded-lg px-4 overflow-hidden")}>
                  <div className="flex relative gap-2 2xl:mb-2">
                    <div className="h-full w-full absolute top-0 left-0">
                      <div className="flex h-max gap-2">
                        {tableTabs.map((tab) =>
                          <div onClick={() => setSelectedTableTab(tab.id)} key={tab.id} className={`px-2 py-0.5 2xl:py-1 bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer ${selectedTableTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                            <h3 className="text-[12px] font-medium">{tab.label}</h3>
                          </div>
                        )}
                      </div>
                    </div>
                    <h3 className="font-bold text-end xl-2xl:text-center w-full text-sm 2xl:text-xl">{graphName}</h3>
                  </div>
                  {selectedTableTab === 'graph' &&
                    <div>
                      <Area
                        item={randomTag}
                        type="tag"
                        areaType="regular"
                        renderType="lazyLoading"
                        maintainAspectRatio={false}
                        areaApiConfig={areaApiConfig}
                      />
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div >
  );
};

export default RPPModal;